import {
  type DeviceEvent,
  FIXED_REPORT_MODE,
  FUNCTION_KEY_MODE,
  GPS_MODE,
  NETWORK_MODE,
  POWER_KEY_MODE,
  type Tracker,
  type TrackerSetting,
} from '@racemap/sdk/schema/trackers';
import { HAIRSP } from '@racemap/utilities/consts/characters';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { NON_MOVE_REPORT_MODE } from '@racemap/utilities/consts/trackers';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import { Popover, Progress } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { PopoverHint } from '../BasicComponents/PopoverHint';
import { PowerKeyModeHint } from '../BasicComponents/PowerKeyModeHint/PowerKeyModeHint';
import { type Column, type Entry, SimpleTable } from '../BasicComponents/SimpleTable';
import { SuccessFailIcon } from '../BasicComponents/SuccessFailIcon';
import { ActivatedDeviceEvents } from './ActivatedDeviceEvents';
import { NetworkAndLteMode } from './NetworkAndLteMode';
import { NetworkOperator } from './NetworkOperator';

export type Props = {
  tracker: Immutable<Tracker>;
};

interface SettingsEntry<K extends keyof TrackerSetting = keyof TrackerSetting> extends Entry {
  id: K;
  value: TrackerSetting[K];
}

export const TrackerSettingsInfos: FC<Props> = ({ tracker }) => {
  const trackerSettings = tracker.setting || {};

  const data = Object.entries(trackerSettings)
    .filter(([_, value]) => value != null)
    .map(([key, value]) => ({
      value,
      id: key,
    })) as Array<SettingsEntry>;

  const columns = [
    {
      header: 'State',
      dataKey: 'id',
      cellRenderer: (c) => {
        if (c == null) return '';

        switch (c) {
          case 'corner':
          case 'enterMovementByCmd':
          case 'powerKeyMode':
          case 'activatedDeviceEvents':
            return (
              <PopoverHint text={TrackerSettingsLabels[c]}>
                {TrackerSettingsDescription[c]}
              </PopoverHint>
            );

          default:
            return TrackerSettingsLabels[c] || 'Unknown';
        }
      },
      key: 'id',
    } as Column<SettingsEntry, 'id'>,
    {
      header: 'Value',
      dataKey: 'value',
      cellRenderer: (c, r) => {
        if (c == null) return;
        switch (r.id) {
          case 'infoReportInterval':
          case 'moveCheckInterval':
          case 'moveSendInterval':
          case 'nonMoveCheckInterval':
          case 'nonMoveSendInterval':
          case 'dnsLookupInterval':
            return `${c}${HAIRSP}s`;
          case 'adjustIntervalsDuringNonMovement':
          case 'reportNonMovement':
          case 'reportMovement':
          case 'nonMovementModeDisabled':
          case 'serverAckEnabled':
          case 'isMotionSensorEnabled':
          case 'multiPacketSendingEnabled':
          case 'smsAckEnabled':
            return <SuccessFailIcon isSuccess={!!c} />;
          case 'heartbeatInterval':
            if (c === 0) return '-';
            return `${c}${HAIRSP}min`;
          case 'nonMoveBegin':
            return typeof c === 'number' ? `${(c * 15) / 60}${HAIRSP}min` : '';
          case 'estimatedTrackInterval':
            return typeof c === 'number' ? `${c / OneSecondInMillis}${HAIRSP}s` : '';
          case 'gpsMode':
            return isNotEmptyString(c) && isGPSMode(c) ? GPSModeLabels[c] : '';
          case 'reportMode':
            return isNotEmptyString(c) && isReportMode(c) ? FixedReportModeLabels[c] : '';
          case 'corner':
            if (c === 0) {
              return 'disabled';
            }
            return `${c}${HAIRSP}°`;
          case 'nmdReportMode':
            return isNotEmptyString(c) && isNmdReportMode(c) ? NonMoveReportModeLabels[c] : '';
          case 'reportDistance':
          case 'reportMileage':
            return `${c}${HAIRSP}m`;
          case 'reportBeginTime':
          case 'reportEndTime':
            return typeof c === 'string' ? `${c.replace(/(..)$/, ':$1')}${HAIRSP}` : c;
          case 'enterMovementByCmd':
          case 'infoReportEnabled':
          case 'heartbeatEnabled':
            return c ? 'enabled' : 'disabled';

          case 'functionKeyMode':
            return isNotEmptyString(c) && isFunctionKeyMode(c) ? FunctionKeyModeLabels[c] : '';
          case 'powerKeyMode':
            return isNotEmptyString(c) && isPowerKeyMode(c) ? PowerKeyModeLabels[c] : '';
          case 'allowedNetworkOperators':
            return Array.isArray(c) ? <NetworkOperator networkOperators={c} /> : '';
          case 'blockedNetworkOperators':
            return Array.isArray(c) ? <NetworkOperator networkOperators={c} /> : '';
          case 'networkMode':
            return isNotEmptyString(c) && isNetworkMode(c) ? (
              <NetworkAndLteMode networkMode={c} />
            ) : (
              ''
            );
          case 'motionSensorSensitivity':
            return typeof c === 'number' ? (
              <Popover content={`${c}/10`}>
                <Progress percent={c * 10} steps={10} showInfo={false} />
              </Popover>
            ) : (
              ''
            );
          case 'activatedDeviceEvents':
            return Array.isArray(c) ? (
              <ActivatedDeviceEvents activatedDeviceEvents={c as Array<DeviceEvent>} />
            ) : (
              ''
            );
          default:
            return `${c}`;
        }
      },
      key: 'value',
    } as Column<SettingsEntry, 'value'>,
  ] as Array<Column<SettingsEntry>>;

  return (
    <SimpleTable
      columns={columns}
      data={data}
      style={{ borderRadius: 'unset', borderTop: 'unset' }}
    />
  );
};

const isGPSMode = (value: string): value is GPS_MODE => {
  return Object.values(GPS_MODE).includes(value as GPS_MODE);
};

const isReportMode = (value: string): value is FIXED_REPORT_MODE => {
  return Object.values(FIXED_REPORT_MODE).includes(value as FIXED_REPORT_MODE);
};

const isNmdReportMode = (value: string): value is NON_MOVE_REPORT_MODE => {
  return Object.values(NON_MOVE_REPORT_MODE).includes(value as NON_MOVE_REPORT_MODE);
};

const isFunctionKeyMode = (value: string): value is FUNCTION_KEY_MODE => {
  return Object.values(FUNCTION_KEY_MODE).includes(value as FUNCTION_KEY_MODE);
};

const isPowerKeyMode = (value: string): value is POWER_KEY_MODE => {
  return Object.values(POWER_KEY_MODE).includes(value as POWER_KEY_MODE);
};

const isNetworkMode = (value: string): value is NETWORK_MODE => {
  return Object.values(NETWORK_MODE).includes(value as NETWORK_MODE);
};

export const TrackerSettingsLabels: Record<keyof TrackerSetting, string> = {
  reportMode: 'Report Mode',
  moveCheckInterval: 'Check Interval',
  moveSendInterval: 'Send Interval',
  corner: 'Corner',
  nonMoveBegin: 'Time to switch into Non-Movement',
  nonMoveCheckInterval: 'Non-Movement Check Interval',
  nonMoveSendInterval: 'Non-Movement Send Interval',
  nmdReportMode: 'Non-Movement Report Mode',
  enterMovementByCmd: 'Enter movement mode by command',
  reportBeginTime: 'Report begin time',
  reportEndTime: 'Report end time',
  reportDistance: 'Report distance',
  reportMileage: 'Report mileage',
  estimatedTrackInterval: 'Estimated Report Interval',
  gpsMode: 'GPS Mode',
  infoReportEnabled: 'Info Report',
  heartbeatEnabled: 'Heartbeat',
  heartbeatInterval: 'Heartbeat Interval',
  infoReportInterval: 'Info Report Interval',
  serverAckEnabled: 'Server ACK',
  powerKeyMode: 'Power Key Mode',
  functionKeyMode: 'Function Key Mode',
  adjustIntervalsDuringNonMovement: 'Adjust Intervals During Non-Movement',
  reportNonMovement: 'Report Non-Movement',
  reportMovement: 'Report Movement',
  nonMovementModeDisabled: 'Non-Movement Mode Disabled',
  allowedNetworkOperators: 'Allowed Network Operators',
  blockedNetworkOperators: 'Blocked Network Operators',
  networkMode: 'Network',
  activatedDeviceEvents: 'Activated Device Events',
  isMotionSensorEnabled: 'Motion Sensor',
  motionSensorSensitivity: 'Motion Sensor Sensitivity',
  mainServerIp: 'Main Server IP/Domain',
  mainServerPort: 'Main Server Port',
  backupServerIp: 'Backup Server IP/Domain',
  backupServerPort: 'Backup Server Port',
  bufferMode: 'Buffer Mode',
  dnsLookupInterval: 'DNS Lookup Interval',
  multiPacketSendingEnabled: 'Multi-Packet Sending',
  serverReportMode: 'Server Report Mode',
  smsAckEnabled: 'SMS ACK',
};

const FixedReportModeLabels: Record<FIXED_REPORT_MODE, string> = {
  [FIXED_REPORT_MODE.NONE]: 'unknown',
  [FIXED_REPORT_MODE.DISABLED]: 'no fixed report',
  [FIXED_REPORT_MODE.ENABLED]: 'fixed time report',
  [FIXED_REPORT_MODE.DISTANCE]: 'fixed distance report',
  [FIXED_REPORT_MODE.MILEAGE]: 'fixed mileage report',
  [FIXED_REPORT_MODE.OPTIMUM]: 'optimum report',
  [FIXED_REPORT_MODE.TIME_OR_MILEAGE]: 'fixed time or mileage report',
};

export const GPSModeLabels: Record<GPS_MODE, string> = {
  [GPS_MODE.ALWAYS_ON]: 'always on',
  [GPS_MODE.NONE]: 'unknown',
  [GPS_MODE.ON_DURING_MOVEMENT]: 'on during movement',
  [GPS_MODE.ON_NEED]: 'on need',
};

const NonMoveReportModeLabels: Record<NON_MOVE_REPORT_MODE, string> = {
  [NON_MOVE_REPORT_MODE.LAST_FIX]: 'last fixed position',
  [NON_MOVE_REPORT_MODE.CURRENT_LOCATION]: 'current position',
  [NON_MOVE_REPORT_MODE.LAST_FIX_AND_CURRENT_LOCATION]: 'first last fix and then current location',
};

const FunctionKeyModeLabels: Record<FUNCTION_KEY_MODE, string> = {
  [FUNCTION_KEY_MODE.NONE]: 'None',
  [FUNCTION_KEY_MODE.DISABLED]: 'Disabled',
  [FUNCTION_KEY_MODE.GEO_FENCE]: 'Geo-fence Mode',
  [FUNCTION_KEY_MODE.SOS]: 'SOS Mode',
  [FUNCTION_KEY_MODE.GEO_FENCE_CURRENT_POSITION]: 'Geo-fence based on location',
  [FUNCTION_KEY_MODE.LOCATION]: 'Location Mode',
  [FUNCTION_KEY_MODE.MIXED]: 'Mixed Mode',
};

export const PowerKeyModeLabels: Record<POWER_KEY_MODE, string> = {
  [POWER_KEY_MODE.NONE]: 'None',
  [POWER_KEY_MODE.NO_POWER_OFF]: 'No Power Off',
  [POWER_KEY_MODE.POWER_OFF]: 'Power Off',
  [POWER_KEY_MODE.SOS_MODE]: 'SOS Mode',
};

const TrackerSettingsDescription: Partial<Record<keyof TrackerSetting, JSX.Element>> = {
  corner: (
    <span>
      This value indicates whether the device should report its location based on changes in its
      direction of movement. 0 - disabled, 1-180 - enabled.
    </span>
  ),
  enterMovementByCmd: (
    <span>
      This value determines if the device should be forced into a movement state after receiving the
      command to report its current position.
    </span>
  ),
  activatedDeviceEvents: (
    <span>
      This value indicates which device events are activated and will be reported by the device.
    </span>
  ),
  powerKeyMode: <PowerKeyModeHint />,
};
